// <SessionProvider config> + useSession(): the auth half of every app's
// AppWrapper.
//
//   valid (unexpired) token        -> render children at once ("signedIn"),
//                                     verify the signature in the background
//                                     (invalid -> signOut)
//   missing / expired token        -> refreshSession(); a token -> "signedIn",
//                                     none -> redirectToSignIn()
//   ~refreshLeadSec before exp     -> silent refresh; if that fails, nothing
//                                     happens until the token really expires,
//                                     then refresh-then-redirect
//
// The token always lives in the shared ID token cookie, so API helpers that
// read the cookie per request pick up a refreshed token without re-rendering.
// No UI here: the app passes its own spinner as `fallback`.

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { configureSession, ResolvedSessionConfig, SessionConfig } from "./config";
import { cookies } from "./cookies";
import { redirectToSignIn, signOut as signOutEverywhere, SignOutOptions } from "./navigation";
import { refreshSession, refreshSessionTokens, SessionTokens } from "./refresh";
import { decodeToken, getToken, isExpired, resolveToken, stripTokenFromUrl, TokenClaims, tokenExpiryMs } from "./token";
import { isVerificationUnavailable, verifyToken } from "./verify";

export type SessionStatus = "checking" | "signedIn" | "signedOut";

export type SessionState = {
	status: SessionStatus;
	token: string | null;
	claims: TokenClaims | null;
	/** this page load got its token from ?token= (back from sign-in / another app) */
	freshLogin: boolean;
};

export type SessionContextValue = SessionState & {
	config: ResolvedSessionConfig;
	/** refresh now (adopts a newer cookie token if another tab already refreshed) */
	refresh: () => Promise<string | null>;
	/**
	 * A fresh ID + access token from /auth/refresh (always a request). The ID
	 * token becomes the session's; the access token is only returned -- keep
	 * it in memory, never store it. null on failure (the session is unchanged).
	 */
	refreshTokens: () => Promise<SessionTokens | null>;
	signOut: (options?: Omit<SignOutOptions, "config">) => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export type SessionProviderProps = {
	config: SessionConfig;
	children?: ReactNode;
	/** rendered while the session is being established or the page is leaving for sign-in */
	fallback?: ReactNode;
	/** background signature check of the token found on load (default true) */
	verify?: boolean;
	/** how long before exp the silent refresh runs (seconds, default 300) */
	refreshLeadSec?: number;
};

// setTimeout fires immediately for delays above 2^31-1 ms (~24.8 days).
const MAX_TIMER_DELAY_MS = 2 ** 31 - 1;
const clampDelay = (ms: number) => Math.min(Math.max(ms, 0), MAX_TIMER_DELAY_MS);

const signedIn = (token: string, freshLogin: boolean): SessionState => ({
	status: "signedIn",
	token,
	claims: decodeToken(token),
	freshLogin,
});

export function SessionProvider({
	config,
	children,
	fallback = null,
	verify = true,
	refreshLeadSec = 300,
}: SessionProviderProps) {
	// configured synchronously, before any child renders or requests
	const configKey = JSON.stringify(config);
	const resolvedRef = useRef<{ key: string; resolved: ResolvedSessionConfig } | null>(null);
	if (!resolvedRef.current || resolvedRef.current.key !== configKey) {
		resolvedRef.current = { key: configKey, resolved: configureSession(config) };
	}
	const resolved = resolvedRef.current.resolved;

	const [initial] = useState(() => resolveToken({ strip: false, config: resolved }));
	const [state, setState] = useState<SessionState>(() =>
		initial.token && !isExpired(initial.token)
			? signedIn(initial.token, initial.freshLogin)
			: { status: "checking", token: null, claims: null, freshLogin: initial.freshLogin }
	);
	const stateRef = useRef(state);
	stateRef.current = state;
	const mountedRef = useRef(true);

	// tokens that came from our own /auth/refresh need no signature check
	const trustedRef = useRef(new Set<string>());
	// tokens whose silent refresh already failed: wait for their expiry instead
	const silentFailedRef = useRef(new Set<string>());
	const verifiedRef = useRef(new Set<string>());

	const setIfMounted = useCallback((next: SessionState) => {
		if (mountedRef.current) setState(next);
	}, []);

	// A newer valid token another tab/app already put on the domain, else /auth/refresh.
	const obtainToken = useCallback(async (): Promise<string | null> => {
		const onDomain = getToken(resolved);
		if (onDomain && onDomain !== stateRef.current.token && !isExpired(onDomain)) return onDomain;
		const refreshed = await refreshSession(resolved);
		if (refreshed) trustedRef.current.add(refreshed);
		return refreshed;
	}, [resolved]);

	const leave = useCallback(
		(reason: string) => {
			setIfMounted({ status: "signedOut", token: null, claims: null, freshLogin: stateRef.current.freshLogin });
			redirectToSignIn(reason, resolved);
		},
		[resolved, setIfMounted]
	);

	const refresh = useCallback(async () => {
		const token = await obtainToken();
		if (token) setIfMounted(signedIn(token, stateRef.current.freshLogin));
		return token;
	}, [obtainToken, setIfMounted]);

	const refreshTokens = useCallback(async () => {
		const tokens = await refreshSessionTokens(resolved);
		if (tokens) {
			trustedRef.current.add(tokens.idToken);
			setIfMounted(signedIn(tokens.idToken, stateRef.current.freshLogin));
		}
		return tokens;
	}, [resolved, setIfMounted]);

	const signOut = useCallback(
		async (options: Omit<SignOutOptions, "config"> = {}) => {
			setIfMounted({ status: "signedOut", token: null, claims: null, freshLogin: false });
			await signOutEverywhere({ ...options, config: resolved });
		},
		[resolved, setIfMounted]
	);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// on load: drop ?token= from the address bar; no usable token -> refresh, else sign in
	useEffect(() => {
		if (initial.source === "query") stripTokenFromUrl();
		if (stateRef.current.status !== "checking") return;
		let cancelled = false;
		obtainToken().then((token) => {
			if (cancelled) return;
			if (token) setIfMounted(signedIn(token, stateRef.current.freshLogin));
			else leave(initial.token ? "token expired" : "no token");
		});
		return () => {
			cancelled = true;
		};
		// once per page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// background signature check of a token we didn't get from /auth/refresh
	useEffect(() => {
		const token = state.token;
		if (!verify || state.status !== "signedIn" || !token) return;
		if (trustedRef.current.has(token) || verifiedRef.current.has(token)) return;
		verifiedRef.current.add(token);
		verifyToken(token, resolved).catch((error) => {
			if (isVerificationUnavailable(error)) {
				console.warn(`[web-session:${resolved.app}] token not verified (JWKS unavailable)`, error?.message ?? error);
				return;
			}
			if (stateRef.current.token !== token) return; // already replaced
			console.warn(`[web-session:${resolved.app}] invalid token, signing out`, error?.message ?? error);
			signOut();
		});
	}, [state.token, state.status, verify, resolved, signOut]);

	// silent refresh before exp; at exp, refresh-then-redirect
	useEffect(() => {
		const token = state.token;
		if (state.status !== "signedIn" || !token) return;
		const expiry = tokenExpiryMs(token);
		if (expiry === null) return;
		const issuedAt = decodeToken(token)?.iat;
		const lifetime = typeof issuedAt === "number" ? expiry - issuedAt * 1000 : Number.POSITIVE_INFINITY;
		// a token shorter-lived than the lead time refreshes at half-life (never in a loop)
		const leadMs = Math.min(refreshLeadSec * 1000, lifetime / 2);
		let busy = false;
		let cancelled = false;

		const silent = async () => {
			if (busy || cancelled || silentFailedRef.current.has(token)) return;
			busy = true;
			const next = await obtainToken();
			busy = false;
			if (cancelled) return;
			if (next) setIfMounted(signedIn(next, stateRef.current.freshLogin));
			else silentFailedRef.current.add(token);
		};

		const atExpiry = async () => {
			if (busy || cancelled) return;
			busy = true;
			const next = await obtainToken();
			busy = false;
			if (cancelled) return;
			if (next) {
				setIfMounted(signedIn(next, stateRef.current.freshLogin));
				return;
			}
			cookies.removeIdToken(resolved);
			leave("session expired");
		};

		const silentTimer = setTimeout(silent, clampDelay(expiry - leadMs - Date.now()));
		const expiryTimer = setTimeout(atExpiry, clampDelay(expiry - Date.now()));

		// timers are late after a sleep / in a background tab: catch up when visible
		const onVisible = () => {
			if (document.visibilityState !== "visible") return;
			const now = Date.now();
			if (expiry <= now) atExpiry();
			else if (expiry - leadMs <= now) silent();
		};
		document.addEventListener("visibilitychange", onVisible);

		return () => {
			cancelled = true;
			clearTimeout(silentTimer);
			clearTimeout(expiryTimer);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [state.token, state.status, refreshLeadSec, obtainToken, leave, resolved, setIfMounted]);

	const value = useMemo<SessionContextValue>(
		() => ({ ...state, config: resolved, refresh, refreshTokens, signOut }),
		[state, resolved, refresh, refreshTokens, signOut]
	);

	return (
		<SessionContext.Provider value={value}>
			{state.status === "signedIn" ? children : fallback}
		</SessionContext.Provider>
	);
}

/** The session; must be used under <SessionProvider>. */
export function useSession(): SessionContextValue {
	const value = useContext(SessionContext);
	if (!value) throw new Error("web-session: useSession() must be used under <SessionProvider>");
	return value;
}

/** The session, or null outside a <SessionProvider> (for components shared with unauthenticated pages). */
export function useOptionalSession(): SessionContextValue | null {
	return useContext(SessionContext);
}
